import type { CvContentBlock } from '@/units/ds/utils/cv-content-block.type'
import type { CvPage } from '@/units/ds/utils/cv-page.type'
import {
  CV_SECTION_GAP,
  CV_SPLITTABLE_BLOCK_TYPES,
} from '@/units/ds/utils/cv-pagination.constants'
import { splitCvBlock } from '@/units/ds/utils/split-cv-block'

/**
 * Distributes measured CV sections over A4 pages.
 *
 * `availableHeight` returns the usable height of a given page, which differs
 * per page because page 1 carries the "Lebenslauf" heading plus the contact
 * block and later pages the "Lebenslauf (weiter)" heading.
 *
 * Sections listed in `CV_SPLITTABLE_BLOCK_TYPES` are cut between entries;
 * everything else moves to the next page as a whole. A section that does not
 * fit on an empty page is placed anyway — an overlong page is a layout glitch,
 * an endless loop freezes the applicant's browser.
 *
 * Always returns at least one page so the template has something to render.
 */
export function paginateCvBlocks(
  blocks: CvContentBlock[],
  availableHeight: (pageIdx: number) => number,
): CvPage[] {
  const result: CvPage[] = []
  let currentPage: CvContentBlock[] = []
  let pageIdx = 0
  let usedHeight = 0

  const startNewPage = () => {
    if (currentPage.length > 0) result.push({ blocks: currentPage })
    currentPage = []
    pageIdx = result.length
    usedHeight = 0
  }

  const allBlocks = [...blocks]
  let i = 0
  while (i < allBlocks.length) {
    const block = allBlocks[i]
    const gapBefore = currentPage.length > 0 ? CV_SECTION_GAP : 0
    const available = availableHeight(pageIdx) - usedHeight

    if (block.height + gapBefore <= available) {
      usedHeight += block.height + gapBefore
      currentPage.push(block)
      i++
      continue
    }

    if (CV_SPLITTABLE_BLOCK_TYPES.includes(block.type)) {
      const { fit, overflow } = splitCvBlock(block, available - gapBefore)

      if (fit) {
        usedHeight += fit.height + gapBefore
        currentPage.push(fit)
      } else if (currentPage.length === 0) {
        // A single entry is taller than a whole empty page. Paging again would
        // land in exactly this state, so keep it here and move on.
        currentPage.push(overflow ?? block)
        startNewPage()
        i++
        continue
      }

      startNewPage()
      if (overflow) {
        allBlocks[i] = overflow
      } else {
        i++
      }
      continue
    }

    if (currentPage.length === 0) {
      // Unsplittable and taller than a whole empty page — see above.
      currentPage.push(block)
      startNewPage()
      i++
      continue
    }

    startNewPage()
  }

  if (currentPage.length > 0) result.push({ blocks: currentPage })
  return result.length > 0 ? result : [{ blocks: [] }]
}
